import {Component, Inject, input, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {debounceTime, distinctUntilChanged, map, Observable, startWith, Subscription, switchMap, tap} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent, MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatChipsModule} from "@angular/material/chips";
import {MatIconModule} from "@angular/material/icon";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {environment} from "../../../../../environments/environment";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute} from "@angular/router";
import {MatListItemTitle, MatListOption, MatNavList, MatSelectionList} from "@angular/material/list";
import {LanguageService} from "../../../../shared/services/language.service";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";

@Component({
  selector: 'app-benchmark',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    NgForOf,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    CardsBasicComponent,
    TranslateModule,
    MatIconButton,
    MatListItemTitle,
    MatCheckbox,
    MatNavList,
    NgIf,
    MatSelectionList,
    MatListOption,
    MatRadioGroup,
    MatRadioButton
  ],
  templateUrl: './benchmark.component.html',
  styleUrl: './benchmark.component.css'
})
export class BenchmarkComponent implements OnInit, OnDestroy {
  productId!: any;
  searchControl = new FormControl();
  searchResults!: Observable<any[]>;
  benchmarkData!: any;
  pageList: any[] = [];
  productList: any[] = [];
  isLoading: boolean = false;
  selectedBenchmark: any;

  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private allStocks: any[] = [];
  private jsonUrl = 'assets/json/alphavantag_listing_status.json';
  private maxResults = 30;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _http: HttpClient,
    private _apiService: DjangoApiService,
    public _dialog: MatDialog,
    private _snackbarService: SnackbarService,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    public _route: ActivatedRoute,) {
  }

  /**
   * Executes the necessary operations when the component is initialized.
   * This method is called automatically by Angular upon component initialization.
   *
   * @returns {void}
   */
  ngOnInit() {
    this.searchSymbol();
    this.getRouteParams()
    this.setupLanguageSubscription()
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.productId = this._route.snapshot.parent?.params['productId'];
      this.loadBenchmarkData();
      this.loadPageList();
      this.loadProductList();
    });
  }

  private loadPageList() {
    this.subscriptions.add(
      this._apiService.fetchPageList(this.productId)
        .subscribe({
          next: (res: any) => {
            this.pageList = res
          },
          error: (error) => {
            console.error('There was an error fetching the pages', error);
          }
        })
    )
  }

  private loadProductList() {
    this.subscriptions.add(
      this._apiService.fetchProductList()
        .subscribe({
          next: (res: any) => {
            this.productList = res
          },
          error: (error) => {
            console.error('There was an error fetching the pages', error);
          }
        })
    )
  }

  /**
   * Retrieves stock data from a JSON URL.
   *
   * @returns {Observable<any[]>} An observable that resolves to an array of stock data.
   */
  getStockData(): Observable<any[]> {
    return this._http.get<any[]>(this.jsonUrl);
  }


  searchSymbol(): void {
    this.subscriptions.add(
      this.getStockData().subscribe((data) => {
        this.allStocks = data; // Daten aus der JSON-Datei

        // Kombiniere JSON-Daten und Produktliste
        this.searchResults = this.searchControl.valueChanges.pipe(
          startWith(''),
          debounceTime(300),
          distinctUntilChanged(),
          map((value: string) => this._filterStocksAndProducts(value))
        );
      })
    );
  }

  private _filterStocksAndProducts(value: string): any[] {
    const filterValue = value.toLowerCase();

    // Filtere die Stocks (aus der JSON-Datei)
    const filteredStocks = this.allStocks.filter(stock =>
      stock.symbol.toLowerCase().includes(filterValue) ||
      stock.name.toLowerCase().includes(filterValue)
    );

    // Filtere die Produkte (aus der Produktliste)
    const filteredProducts = this.productList.filter(product =>
      product.translations[this.currentLanguage]?.title?.toLowerCase().includes(filterValue)
    ).map(product => ({
      ...product,
      type: 'product'
    }));

    // Kombiniere beide Listen und begrenze die Ergebnisse
    return [
      ...filteredStocks.map(stock => ({ ...stock, type: 'benchmark' })),
      ...filteredProducts
    ].slice(0, this.maxResults);
  }


  /**
   * Loads the benchmark data for the product.
   *
   * If the product data is available, this method will make an API request to fetch the benchmark list
   * for the product and store the response in the benchmarkData property.
   *
   * @return {void}
   */
  loadBenchmarkData() {
    this.subscriptions.add(
      this._apiService.fetchBenchmarkList(this.productId).subscribe({
        next: (res: any) => {
          this.benchmarkData = res;
          this.selectedBenchmark = this.benchmarkData.find((b:any) => b.use_for_statistics);
        },
        error: (error) => {
          console.error('There was an error fetching the user information', error);
        }
      }));
  }

  /**
   * Deletes the specified benchmark.
   *
   * @param {any} data - The benchmark to delete.
   * @return {void}
   */
  deleteBenchmark(data: any): void {
    this.isLoading = true
    this.subscriptions.add(
      this._apiService.deleteBenchmark(data.id).subscribe({
        next: (res: any) => {
          this.loadBenchmarkData();
          this._snackbarService.show(res.message);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  /**
   * Open a dialog with the given data and perform an action based on the dialog result.
   *
   * @param {any} data - The data to be passed to the dialog.
   * @return {void}
   */
  openDialog(data: any): void {
     this.searchControl.reset();
    const dialogRef = this._dialog.open(DialogBenchmark, {
      data: data,
    });
       dialogRef.afterClosed().subscribe(result => {
         this.createBenchmark(result);
       });
  }

  /**
   * Creates a benchmark using the provided data.
   *
   * @param {any} data - The data used to create the benchmark.
   * @returns {void}
   */
  createBenchmark(data: any) {
    let formattedBenchmark;

    if(data.type === 'product'){
      formattedBenchmark = {
        name: data.title,
        product_as_benchmark: data.id,
        product: this.productId
      }
    } else {
      formattedBenchmark = {
        name: data.name,
        symbol: data.symbol,
        product: this.productId
      }
    }

    this.isLoading = true;
    this.subscriptions.add(
      this._apiService.createBenchmark(formattedBenchmark).subscribe({
        next: (res: any) => {
          this._snackbarService.show(res.message);
          this.loadBenchmarkData();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error('There was an error creating the product', error);
        }
      })
    );
  }

  onBenchmarkChange(selectedBenchmark: any) {
    // Update the `use_for_statistics` property for all benchmarks
    this.benchmarkData = this.benchmarkData.map((benchmark:any) => ({
      ...benchmark,
      use_for_statistics: benchmark === selectedBenchmark
    }));

    // API-Aufruf zum Batch-Update (optional, falls nötig)
    this.updateAllBenchmarks(this.benchmarkData);
  }

  updateAllBenchmarks(benchmarks: any[]) {
    this.isLoading = true;
    this._apiService.updateAllBenchmarks(benchmarks).subscribe({
      next: (res: any) => {
        this._snackbarService.show(res.message);
        this.loadBenchmarkData(); // Daten erneut laden
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Fehler beim Aktualisieren der Benchmarks', error);
      }
    });
  }


  isBenchmarkAssignedToPage(page: any, benchmark: any): boolean {
    return page.benchmarks.some((p: any) => p.id === benchmark.id);
  }

  toggleBenchmarkAssignment(page: any, benchmark: any, isChecked: boolean): void {
    if (isChecked) {
      this.addBenchmarkToPage(page, benchmark)
    } else {
      this.removeBenchmarkFromPage(page, benchmark)
    }
  }

  private addBenchmarkToPage(page: any, benchmark: any): void {
    this.subscriptions.add(
      this._apiService.addBenchmarkToPage(page.id, benchmark.id)
        .subscribe({
          next: (response: any) => {
            page.benchmarks.push(benchmark);
            this._snackbarService.show(response.message);
          },
          error: (error) => {
            console.error('Failed to add period to page', error);
          }
        }));
  }

  private removeBenchmarkFromPage(page: any, benchmark: any): void {
    this.subscriptions.add(
      this._apiService.removeBenchmarkFromPage(page.id, benchmark.id)
        .subscribe({
          next: (response: any) => {
            this._snackbarService.show(response.message);
            const index = page.benchmarks.findIndex((p: any) => p.id === benchmark.id);
            if (index !== -1) {
              page.benchmarks.splice(index, 1);
            }
          },
          error: (error) => {
            console.error('Failed to remove period from page', error);
          }
        }));
  }

  /**
   * Method to unsubscribe from all subscriptions.
   * It should be called when the component or directive is about to be destroyed.
   *
   * @returns {void}
   */
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}


@Component({
  selector: 'dialog-benchmark',
  templateUrl: 'dialog-benchmark.html',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatButton, TranslateModule,],
})
export class DialogBenchmark {
  constructor(
    public dialogRef: MatDialogRef<DialogBenchmark>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
